import type { PluginAPI } from 'tailwindcss/types/config';

const OL_NUMERAL_WIDTH = '40px';
const OL_LI_PADDING_LEFT = '18px';

const tailwindTypographyConfig = (theme: PluginAPI['theme']) => ({
  DEFAULT: {
    css: {
      color: theme('colors.blue.950'),
      lineHeight: theme('lineHeight.relaxed'),
      fontSize: theme('fontSize.lg'),
      h2: {
        marginBottom: '1.143em'
      },
      h3: {
        marginTop: '1.8333em',
        marginBottom: '1em'
      },
      '* > strong': {
        fontWeight: theme('fontWeight.bold')
      },
      'div:empty, p:empty, strong:empty, li:empty': {
        display: 'none'
      },
      'ul li::marker': {
        color: theme('colors.blue.500'),
        fontSize: '1.2em'
      },
      ol: {
        counterReset: 'number-counter',
        paddingLeft: OL_NUMERAL_WIDTH
      },
      'ol > li': {
        counterIncrement: 'number-counter',
        position: 'relative',
        marginTop: '40px',
        marginBottom: '40px',
        paddingLeft: OL_LI_PADDING_LEFT
      },
      'ol > li::before': {
        content: 'counter(number-counter)',
        color: theme('colors.white'),
        fontWeight: theme('fontWeight.bold'),
        position: 'absolute',
        backgroundColor: theme('colors.blue.500'),
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        left: `-${OL_NUMERAL_WIDTH}`,
        lineHeight: OL_NUMERAL_WIDTH,
        width: OL_NUMERAL_WIDTH,
        height: OL_NUMERAL_WIDTH,
        top: '-4px',
        borderRadius: theme('borderRadius.full')
      },
      // Make How-To images:
      // -justified to left edge of article
      // -full-width mobile
      'ol > li > img': {
        marginLeft: `calc(-${OL_NUMERAL_WIDTH} + -${OL_LI_PADDING_LEFT})`,
        marginBottom: '56px',
        [`@media (max-width: ${theme('screens.md')})`]: {
          marginBottom: '48px',
          maxWidth: 'none',
          width: `calc(100% + (${OL_NUMERAL_WIDTH} + ${OL_LI_PADDING_LEFT}))`
        }
      },
      'ol > .ad-slot-container': {
        marginLeft: `-${OL_NUMERAL_WIDTH}`,
        [`@media (max-width: ${theme('screens.md')})`]: {
          maxWidth: 'none',
          width: `calc(100% + ${OL_NUMERAL_WIDTH})`
        }
      },
      'ol > .offer-inblog': {
        marginLeft: `-${OL_NUMERAL_WIDTH}`,
        [`@media (max-width: ${theme('screens.md')})`]: {
          maxWidth: 'none',
          width: `calc(100% + ${OL_NUMERAL_WIDTH})`
        }
      },
      'table td a': {
        fontWeight: theme('fontWeight.medium') + '!important'
      },
      'table td:not(:first-child), table th:not(:first-child)': {
        paddingLeft: '16px',
        [`@media (min-width: ${theme('screens.md')})`]: {
          paddingLeft: '32px'
        }
      },
      'table td:not(:last-child), table th:not(:last-child)': {
        paddingRight: '16px',
        [`@media (min-width: ${theme('screens.md')})`]: {
          paddingRight: '32px'
        }
      },
      '.content-wtk': {
        borderColor: theme('borderColor.gray.300'),
        borderWidth: '1px',
        borderStyle: 'solid',
        borderRadius: theme('borderRadius.md'),
        borderLeftWidth: '0',
        position: 'relative',
        boxShadow: `6px 0 ${theme('colors.green.500')} inset`,
        padding: '16px 16px 16px 24px',
        marginTop: '32px',
        marginBottom: '32px',
        [`@media (min-width: ${theme('screens.md')})`]: {
          borderRadius: theme('borderRadius.lg'),
          padding: '24px 32px',
          marginBottom: '40px'
        }
      },
      // Spacing resets for nested content
      '.content-wtk > *:first-child, table td > *:first-child': {
        marginTop: 0,
        paddingTop: 0
      },
      '.content-wtk > *:last-child, table td > *:last-child': {
        marginBottom: 0,
        paddingBottom: 0
      },
      '.content-wtk h3': {
        fontSize: theme('fontSize.lg') + '!important',
        marginBottom: '10px'
      },
      '.content-wtk strong:first-child': {
        display: 'block',
        marginBottom: '10px'
      },
      '.content-wtk:first-child,.content-wtk:nth-last-child(-n+4)': {
        padding: '16px 20px 16px 12px',
        boxShadow: 'none',
        borderLeftWidth: '1px'
      },
      // 'What To Know' box styles –
      // Consider .content-wtk the What To Know box if is 1st element of article
      '.content-wtk:first-child': {
        marginTop: '8px',
        borderColor: theme('borderColor.green.500'),
        fontWeight: theme('fontWeight.medium')
      },
      // FAQ box styles –
      // Consider .content-wtk the FAQ box if is within last 4 elements of article
      '.content-wtk:nth-last-child(-n+4)': {
        padding: '16px 20px 16px 12px',
        marginTop: '40px',
        [`@media (min-width: ${theme('screens.md')})`]: {
          marginTop: '64px',
          padding: '24px 40px 24px 20px'
        }
      },
      '.content-wtk:first-child ul, .content-wtk:nth-last-child(-n+4) ul': {
        marginBottom: '0'
      },
      '.content-wtk:first-child li::marker': {
        color: theme('colors.green.500')
      },
      '.content-wtk:first-child ul li strong:first-child': {
        fontWeight: theme('fontWeight.medium')
      },
      '.content-wtk:first-child h3, .content-wtk:nth-last-child(-n+4) h3': {
        display: 'inline',
        padding: '0 8px 0 8px',
        position: 'absolute',
        fontWeight: theme('fontWeight.bold'),
        top: '-15px',
        background: theme('backgroundColor.white'),
        left: '20px',
        margin: '0'
      },
      '.content-wtk:first-child h3': {
        fontSize: theme('fontSize.lg') + '!important',
        color: theme('colors.green.500')
      },
      '.content-wtk:nth-last-child(-n+4) h3': {
        fontSize: theme('fontSize.2xl[0]') + '!important',
        [`@media (min-width: ${theme('screens.md')})`]: {
          fontSize: theme('fontSize.4xl[0]') + '!important',
          left: '24px',
          top: '-18px'
        }
      },
      '.content-wtk:nth-last-child(-n+4) strong:first-of-type': {
        marginBottom: '12px',
        display: 'block'
      },
      // Hide br tags used for spacing after FAQ question
      '.content-wtk:nth-last-child(-n+4) strong + br': {
        display: 'none'
      },
      '.content-wtk:nth-last-child(-n+4) ul > li': {
        marginTop: '16px',
        marginBottom: '16px'
      },
      '.content-wtk:nth-last-child(-n+4) + *': {
        marginTop: '44px'
      },
      '.content-warning': {
        borderColor: theme('colors.red'),
        boxShadow: `6px 0 ${theme('colors.red')} inset`
      },
      '.content-warning > h3, .content-warning > strong': {
        color: theme('colors.red')
      },
      '.content-warning > h3::before, .content-warning > strong::before': {
        content: `url(
                'data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMjQgMjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTEgMjEgMTIgMmwxMSAxOUgxWm0xMS0zYS45Ny45NyAwIDAgMCAuNzEzLS4yODhBLjk2OC45NjggMCAwIDAgMTMgMTdhLjk2OC45NjggMCAwIDAtLjI4Ny0uNzEyQS45NjguOTY4IDAgMCAwIDEyIDE2YS45NjguOTY4IDAgMCAwLS43MTMuMjg4QS45NjguOTY4IDAgMCAwIDExIDE3YzAgLjI4My4wOTYuNTIuMjg3LjcxMi4xOTIuMTkyLjQzLjI4OC43MTMuMjg4Wm0tMS0zaDJ2LTVoLTJ2NVoiIGZpbGw9IiNFMjI1MUIiLz48L3N2Zz4='
              )`,
        height: '24px',
        width: '24px',
        display: 'inline-block',
        verticalAlign: 'sub',
        marginRight: '8px'
      }
    }
  }
});

export default tailwindTypographyConfig;
